import { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "../lib/cart";
import { useCurrency } from "../lib/currency";
import { LogoMark } from "./Logo";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const { format } = useCurrency();
  const [qty, setQty] = useState(1);
  const [imgOk, setImgOk] = useState(true);
  const inCart = useCart().items.some((i) => i.id === product.id);

  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`product-card-${product.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-1 hover:border-emerald-600/30 hover:shadow-xl hover:shadow-emerald-900/10"
    >
      <div className="relative aspect-square overflow-hidden bg-[#D8EFE6]">
        {imgOk ? (
          <img
            src={product.image}
            alt={`${product.name} ${product.strength} pack`}
            loading="lazy"
            onError={() => setImgOk(false)}
            className="h-full w-full object-cover transition-[transform] duration-700 ease-out group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#D8EFE6] to-[#BFE4D7]">
            <LogoMark className="h-14 w-14" />
            <span className="px-6 text-center font-display text-sm font-bold text-emerald-900">
              {product.name} {product.strength}
            </span>
            <span className="font-mono text-xs text-emerald-700">{product.pack_size} pack</span>
          </div>
        )}
        <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 font-mono text-[11px] font-medium text-emerald-800 backdrop-blur-sm">
          {product.category}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-lg font-bold leading-tight text-slate-900">
            {product.name} <span className="font-semibold text-slate-500">{product.strength}</span>
          </h3>
          <span className="whitespace-nowrap rounded-lg bg-emerald-50 px-2.5 py-1 font-mono text-sm font-bold text-emerald-800">
            {format(product.price)}
          </span>
        </div>
        <p className="font-mono text-xs text-slate-500">{product.pack_size} pack</p>

        <div className="mt-auto flex items-center gap-2 pt-2">
          <div className="flex items-center rounded-lg border border-slate-200">
            <button
              type="button"
              data-testid={`product-qty-minus-${product.id}`}
              aria-label="Decrease quantity"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="px-2.5 py-2 text-slate-500 transition-[color] hover:text-emerald-700"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span data-testid={`product-qty-${product.id}`} className="w-6 text-center font-mono text-sm font-semibold text-slate-800">
              {qty}
            </span>
            <button
              type="button"
              data-testid={`product-qty-plus-${product.id}`}
              aria-label="Increase quantity"
              onClick={() => setQty((q) => Math.min(99, q + 1))}
              className="px-2.5 py-2 text-slate-500 transition-[color] hover:text-emerald-700"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
          <button
            type="button"
            data-testid={`product-add-${product.id}`}
            onClick={() => addItem(product, qty)}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#0D5C46] px-3 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-[#0A4837] active:scale-[0.98]"
          >
            <ShoppingBag className="h-4 w-4" />
            {inCart ? "Add More" : "Add to Enquiry"}
          </button>
        </div>
      </div>
    </motion.article>
  );
};
